import { ChatViewState, PromptTurn } from '@type/chat';

type AdjacentTurnOffset = -1 | 1;

interface ResolveRestoreActionArgs {
  savedViewState: ChatViewState | undefined;
  turns: PromptTurn[];
}

interface ResolveInitialActiveTurnArgs {
  savedViewState: ChatViewState | undefined;
}

interface ResolveAdjacentTurnArgs {
  turns: PromptTurn[];
  activeTurnStartMessageId: string | undefined;
  offset: AdjacentTurnOffset;
}

interface ProgrammaticScrollState {
  messageId: string;
  targetScrollTop: number;
}

interface ResolveProgrammaticScrollStateArgs {
  programmaticScrollState: ProgrammaticScrollState | undefined;
  currentScrollTop: number;
  tolerance?: number;
}

interface RestoreActionBase {
  fallbackActiveTurnStartMessageId: string | undefined;
}

export type RestoreAction =
  | ({ type: 'sync-from-viewport' } & RestoreActionBase)
  | ({ type: 'restore-scroll-top'; scrollTop: number } & RestoreActionBase)
  | ({ type: 'restore-anchor'; anchorMessageId: string } & RestoreActionBase)
  | ({ type: 'sync-from-saved-active-turn' } & RestoreActionBase);

export type ProgrammaticScrollResolution =
  | { type: 'no-lock' }
  | { type: 'keep-lock'; messageId: string; targetScrollTop: number }
  | { type: 'release-lock'; messageId: string; targetScrollTop: number };

const getFirstTurnStartMessageId = (turns: PromptTurn[]): string | undefined => turns[0]?.startMessageId;

const getFallbackActiveTurnStartMessageId = (
  turns: PromptTurn[],
  savedViewState?: ChatViewState
): string | undefined => savedViewState?.activeTurnStartMessageId ?? getFirstTurnStartMessageId(turns);

export const resolveInitialActiveTurn = ({
  savedViewState,
}: ResolveInitialActiveTurnArgs): string | undefined => savedViewState?.activeTurnStartMessageId;

export const resolveRestoreAction = ({
  savedViewState,
  turns,
}: ResolveRestoreActionArgs): RestoreAction => {
  const fallbackActiveTurnStartMessageId = getFallbackActiveTurnStartMessageId(turns, savedViewState);

  if (!savedViewState) {
    return {
      type: 'sync-from-viewport',
      fallbackActiveTurnStartMessageId,
    };
  }

  if (savedViewState.scrollTop > 0) {
    return {
      type: 'restore-scroll-top',
      scrollTop: savedViewState.scrollTop,
      fallbackActiveTurnStartMessageId,
    };
  }

  if (savedViewState.anchorMessageId) {
    return {
      type: 'restore-anchor',
      anchorMessageId: savedViewState.anchorMessageId,
      fallbackActiveTurnStartMessageId,
    };
  }

  return {
    type: 'sync-from-saved-active-turn',
    fallbackActiveTurnStartMessageId,
  };
};

export const resolveAdjacentTurn = ({
  turns,
  activeTurnStartMessageId,
  offset,
}: ResolveAdjacentTurnArgs): { currentIndex: number; nextIndex: number; turn: PromptTurn | undefined } => {
  if (turns.length === 0) {
    return {
      currentIndex: -1,
      nextIndex: -1,
      turn: undefined,
    };
  }

  const activeTurnIndex = turns.findIndex(
    (turn) => turn.startMessageId === activeTurnStartMessageId
  );
  const currentIndex = activeTurnIndex >= 0 ? activeTurnIndex : 0;
  const nextIndex = Math.min(Math.max(currentIndex + offset, 0), turns.length - 1);

  return {
    currentIndex,
    nextIndex,
    turn: turns[nextIndex],
  };
};

export const resolveProgrammaticScrollState = ({
  programmaticScrollState,
  currentScrollTop,
  tolerance = 4,
}: ResolveProgrammaticScrollStateArgs): ProgrammaticScrollResolution => {
  if (!programmaticScrollState) {
    return { type: 'no-lock' };
  }

  if (Math.abs(currentScrollTop - programmaticScrollState.targetScrollTop) > tolerance) {
    return {
      type: 'keep-lock',
      messageId: programmaticScrollState.messageId,
      targetScrollTop: programmaticScrollState.targetScrollTop,
    };
  }

  return {
    type: 'release-lock',
    messageId: programmaticScrollState.messageId,
    targetScrollTop: programmaticScrollState.targetScrollTop,
  };
};

export type { ProgrammaticScrollState, AdjacentTurnOffset };